import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { auth, db, googleProvider } from "../firebase";
import { onAuthStateChanged, signInWithPopup, signInWithRedirect, signOut } from "firebase/auth";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";

export default function CommentModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [user, setUser] = useState<any>(auth.currentUser);
  const [text, setText] = useState("");
  const [error, setError] = useState("");
  const [rating, setRating] = useState<number>(0);
  const [hoverRating, setHoverRating] = useState<number>(0);

  React.useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => setUser(u));
    return () => unsub();
  }, []);

  // Auto-prompt login when modal opens and user is not logged in
  React.useEffect(() => {
    const tryLogin = async () => {
      if (!open || user) return;
      try {
        await signInWithPopup(auth, googleProvider);
      } catch (e: any) {
        if (e?.code === "auth/popup-blocked") {
          await signInWithRedirect(auth, googleProvider);
        } else if (e?.code === "auth/operation-not-allowed") {
          setError("Login Google belum diaktifkan di Firebase (operation-not-allowed).");
        }
      }
    };
    tryLogin();
  }, [open, user]);

  const login = async () => {
    setError("");
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (e: any) {
      if (e?.code === "auth/popup-blocked") {
        await signInWithRedirect(auth, googleProvider);
        return;
      }
      setError(e?.message || "Gagal login. Pastikan provider Google diaktifkan.");
    }
  };

  const logout = async () => { await signOut(auth); };

  const submit = async () => {
    const c = text.trim();
    if (!user || !c || !rating) return;
    await addDoc(collection(db, "comments"), {
      uid: user.uid,
      name: user.displayName || user.email || "Anon",
      photoURL: user.photoURL || null,
      comment: c,
      rating,
      createdAt: serverTimestamp(),
    });
    setText("");
    setRating(0);
    onClose();
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-[100] flex items-center justify-center px-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
          <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }} className="relative w-full max-w-xl mx-auto">
            <div className="absolute -inset-1 bg-gradient-to-r from-cyan-500/25 via-purple-500/25 to-pink-500/25 rounded-3xl blur" />
            <div className="relative w-full rounded-3xl border border-white/15 bg-gradient-to-br from-[#101827] to-[#0b1220] p-6 shadow-xl">
              <div className="flex items-center justify-between mb-4">
                <div className="text-lg font-semibold">Tulis Komentar</div>
                <button onClick={onClose} className="text-xs px-3 py-1 rounded-lg bg-white/10 border border-white/15 hover:bg-white/20">Tutup</button>
              </div>
            {error && <div className="mb-3 text-xs text-red-300 bg-red-500/10 border border-red-400/30 rounded-xl px-3 py-2">{error}</div>}
            {user ? (
              <>
                <div className="mb-3 text-sm text-white/80">Login sebagai <span className="font-semibold text-cyan-300">{user.displayName || user.email}</span> <button onClick={logout} className="ml-2 text-xs px-2 py-1 rounded-lg bg-white/10 border border-white/15 hover:bg-white/20">Logout</button></div>
                <div className="mb-3">
                  <div className="text-sm opacity-80 mb-2">Beri rating</div>
                  <div className="flex items-center gap-2">
                    {[1,2,3,4,5].map(n => (
                      <button
                        key={n}
                        type="button"
                        onMouseEnter={() => setHoverRating(n)}
                        onMouseLeave={() => setHoverRating(0)}
                        onClick={() => setRating(n)}
                        aria-label={`rating-${n}`}
                        className="w-8 h-8 rounded-md border border-white/10 bg-white/5 hover:bg-white/10 flex items-center justify-center"
                      >
                        <svg viewBox="0 0 24 24" className={(hoverRating >= n || rating >= n) ? 'w-5 h-5 text-yellow-400 fill-yellow-400' : 'w-5 h-5 text-white/40'}>
                          <path d="M12 .587l3.668 7.431 8.2 1.193-5.934 5.786 1.401 8.169L12 18.896l-7.335 3.87 1.401-8.169L.132 9.211l8.2-1.193z"/>
                        </svg>
                      </button>
                    ))}
                  </div>
                </div>
                <label className="text-sm opacity-80">Tulis komentar</label>
                <textarea value={text} onChange={(e) => setText(e.target.value)} className="mt-2 w-full rounded-2xl bg-black/30 border border-white/15 p-4 min-h-[120px] outline-none focus:ring-2 focus:ring-fuchsia-400/40" placeholder="Komentar Anda..." />
                <div className="flex justify-end">
                  <button onClick={submit} disabled={!text.trim() || !rating} className="mt-3 rounded-2xl px-6 py-3 bg-gradient-to-r from-fuchsia-500 to-cyan-500 hover:from-fuchsia-600 hover:to-cyan-600 disabled:opacity-50">Kirim</button>
                </div>
              </>
            ) : (
              <div className="flex items-center justify-between">
                <div className="text-sm text-white/70">Silakan login untuk menulis komentar.</div>
                <button onClick={login} className="text-sm px-4 py-2 rounded-xl bg-gradient-to-r from-fuchsia-500 to-cyan-500 hover:from-fuchsia-600 hover:to-cyan-600">Login Google</button>
              </div>
            )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

